import React, { useState, useCallback, useEffect } from "react";
import useStyles from "./style";
import { Button, Grid, Box } from "@mui/material";
import {
  ContentCopyOutlined,
  TableChartOutlined,
  DescriptionOutlined,
  PictureAsPdfOutlined,
  FileDownloadOutlined,
  AddOutlined,
  Edit,
  Delete,
} from "@mui/icons-material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import jsPDF from "jspdf";
import "jspdf-autotable";
import _ from "lodash";
import BookingApi from "Services/Booking";
import BookingCreateModal from "Pages/Modals/Booking/Create";
import BookingDeleteModal from "Pages/Modals/Booking/Delete";

const Booking = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const [row, setRow] = useState([]);
  const [bulkrow, setBulkrow] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [addBookingModal, setAddBookingModal] = useState(false);
  const [deleteBookingModal, setDeleteBookingModal] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState("");

  const normalizeBookingList = (res) => {
    const payload = res?.data;
    if (Array.isArray(payload?.data?.bookings)) {
      return {
        rows: payload.data.bookings || [],
        total: payload?.data?.total || payload.data.bookings.length || 0,
      };
    }
    if (Array.isArray(payload?.bookings)) {
      return {
        rows: payload.bookings || [],
        total: payload?.total || payload.bookings.length || 0,
      };
    }
    return { rows: [], total: 0 };
  };

  const getBookings = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    BookingApi.getBookingList({ page, limit: pageSize, search: searchText }).then(
      (res) => {
        dispatch(hideLoader());
        const normalized = normalizeBookingList(res);
        setTotalPages(normalized.total || 1);
        setRow(normalized.rows || []);
      },
      (err) => {
        dispatch(hideLoader());
        dispatch(
          showSnackbar({
            message: err?.response?.data?.message || "Failed to fetch data",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      }
    );
  }, [dispatch, page, pageSize, searchText]);

  const getAllBookings = useCallback(() => {
    BookingApi.getBookingList({ page: 1, limit: "unlimited", search: "" }).then(
      (res) => {
        const normalized = normalizeBookingList(res);
        setBulkrow(normalized.rows || []);
      },
      () => setBulkrow([])
    );
  }, []);

  useEffect(() => {
    getBookings();
    getAllBookings();
  }, [getBookings, getAllBookings]);

  const calculateSno = (pageIndex, pageLimit, index) => (pageIndex - 1) * pageLimit + index + 1;

  const dataRows = (row || []).map((data, index) => ({
    id: data?.id || index + 1,
    sno: calculateSno(page, pageSize, index),
    bookingId: data?.id || "-",
    rideId: data?.ride_id || data?.rides?.id || "-",
    passenger: `${data?.users?.first_name || ""} ${data?.users?.last_name || ""}`.trim() || "-",
    pickupPlace: data?.ride_waypoints_bookings_pickup_waypoint_idToride_waypoints?.places?.name || "-",
    dropoffPlace: data?.ride_waypoints_bookings_dropoff_waypoint_idToride_waypoints?.places?.name || "-",
    seatsBooked: data?.seats_booked ?? 0,
    totalAmount: `${data?.total_amount || "0"} ${data?.currency || ""}`.trim(),
    serviceFee: `${data?.service_fee || "0"} ${data?.currency || ""}`.trim(),
    status: data?.status || "-",
    rideStatus: data?.rides?.status || "-",
    createdAt: data?.created_at ? new Date(data.created_at).toLocaleString() : "-",
    raw: data,
  }));

  const formatted = dataRows.map((r) => ({
    "S.NO": r.sno,
    "Booking ID": r.bookingId,
    "Ride ID": r.rideId,
    Passenger: r.passenger,
    "Pickup Place": r.pickupPlace,
    "Dropoff Place": r.dropoffPlace,
    Seats: r.seatsBooked,
    "Total Amount": r.totalAmount,
    "Service Fee": r.serviceFee,
    Status: r.status,
    "Ride Status": r.rideStatus,
    Created: r.createdAt,
  }));

  const bulkFormatted = _.orderBy(bulkrow || [], ["created_at"], ["asc"]).map((data, index) => ({
    "S.NO": index + 1,
    "Booking ID": data?.id || "-",
    "Ride ID": data?.ride_id || data?.rides?.id || "-",
    Passenger: `${data?.users?.first_name || ""} ${data?.users?.last_name || ""}`.trim() || "-",
    "Pickup Place": data?.ride_waypoints_bookings_pickup_waypoint_idToride_waypoints?.places?.name || "-",
    "Dropoff Place": data?.ride_waypoints_bookings_dropoff_waypoint_idToride_waypoints?.places?.name || "-",
    Seats: data?.seats_booked ?? 0,
    "Total Amount": `${data?.total_amount || "0"} ${data?.currency || ""}`.trim(),
    "Service Fee": `${data?.service_fee || "0"} ${data?.currency || ""}`.trim(),
    Status: data?.status || "-",
    "Ride Status": data?.rides?.status || "-",
    Created: data?.created_at ? new Date(data.created_at).toLocaleString() : "-",
  }));

  function CustomToolbar() {
    return (
      <Box sx={{ p: 1, pb: 0, display: "flex", justifyContent: "flex-start" }}>
        <GridToolbarQuickFilter debounceMs={800} autoFocus />
        <GridToolbarContainer>
          <GridToolbarDensitySelector />
        </GridToolbarContainer>
      </Box>
    );
  }

  const handleEdit = (bookingRow) => {
    const bookingId = bookingRow?.id || bookingRow?._id;
    if (!bookingId) {
      dispatch(
        showSnackbar({
          message: "Booking ID not found",
          autoHideDuration: 2500,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
      return;
    }
    setSelectedBookingId(bookingId);
    setAddBookingModal(true);
  };
  const handleDelete = (bookingRow) => {
    const bookingId = bookingRow?.id || bookingRow?._id;
    if (!bookingId) {
      dispatch(
        showSnackbar({
          message: "Booking ID not found",
          autoHideDuration: 2500,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
      return;
    }
    setSelectedBookingId(bookingId);
    setDeleteBookingModal(true);
  };
  const handleAdd = () => {
    setSelectedBookingId("");
    setAddBookingModal(true);
  };
  const handleCloseAdd = (created = false) => {
    setAddBookingModal(false);
    setSelectedBookingId("");
    if (created) {
      getBookings();
      getAllBookings();
    }
  };
  const handleCloseDelete = (deleted = false) => {
    setDeleteBookingModal(false);
    setSelectedBookingId("");
    if (deleted) {
      getBookings();
      getAllBookings();
    }
  };

  const handleCopy = () => {
    const header = Object.keys(formatted[0] || {}).join("\t");
    const rows = formatted.map((r) => Object.values(r).join("\t")).join("\n");
    navigator.clipboard.writeText(`${header}\n${rows}`);
  };
  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(formatted);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Bookings");
    XLSX.writeFile(workbook, "BookingList.xlsx");
  };
  const handleDownloadCSV = () => {
    const csv = Papa.unparse(formatted);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "BookingList.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.autoTable({
      head: [["S.NO", "Booking ID", "Ride ID", "Passenger", "Pickup Place", "Dropoff Place", "Seats", "Total Amount", "Service Fee", "Status", "Ride Status", "Created"]],
      body: formatted.map((r) => Object.values(r)),
    });
    doc.save("BookingList.pdf");
  };
  const handleDownloadAll = () => {
    const csv = Papa.unparse(bulkFormatted);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "All BookingList.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadgeStyle = (value) => {
    const key = String(value || "").toLowerCase();
    const map = {
      approved: { bg: "#14b8a6", fg: "#ffffff" },
      active: { bg: "#10b981", fg: "#ffffff" },
      published: { bg: "#10b981", fg: "#ffffff" },
      confirmed: { bg: "#10b981", fg: "#ffffff" },
      completed: { bg: "#0ea5e9", fg: "#ffffff" },
      onboarding: { bg: "#ef4444", fg: "#ffffff" },
      boarding: { bg: "#ef4444", fg: "#ffffff" },
      cancelled: { bg: "#ef4444", fg: "#ffffff" },
      rejected: { bg: "#ef4444", fg: "#ffffff" },
      pending: { bg: "#f59e0b", fg: "#111827" },
      draft: { bg: "#6b7280", fg: "#ffffff" },
      full: { bg: "#7c3aed", fg: "#ffffff" },
      departed: { bg: "#2563eb", fg: "#ffffff" },
      arrived: { bg: "#14b8a6", fg: "#ffffff" },
      inactive: { bg: "#9ca3af", fg: "#111827" },
    };
    return map[key] || { bg: "#64748b", fg: "#ffffff" };
  };

  const columns = [
    { field: "sno", headerName: "S.No", minWidth: 70, sortable: false, disableColumnMenu: true },
    { field: "bookingId", headerName: "Booking ID", minWidth: 220, sortable: true },
    { field: "rideId", headerName: "Ride ID", minWidth: 220, sortable: true },
    { field: "passenger", headerName: "Passenger", minWidth: 160, sortable: true },
    { field: "pickupPlace", headerName: "Pickup", minWidth: 160, sortable: true },
    { field: "dropoffPlace", headerName: "Dropoff", minWidth: 160, sortable: true },
    { field: "seatsBooked", headerName: "Seats", minWidth: 90, sortable: true },
    { field: "totalAmount", headerName: "Total Amount", minWidth: 120, sortable: true },
    { field: "serviceFee", headerName: "Service Fee", minWidth: 120, sortable: true },
    {
      field: "status",
      headerName: "Status",
      minWidth: 130,
      sortable: true,
      renderCell: (params) => {
        const tone = getStatusBadgeStyle(params?.value);
        return <span style={{ background: tone.bg, color: tone.fg, borderRadius: 999, padding: "5px 10px", fontSize: 12, fontWeight: 700, textTransform: "uppercase", lineHeight: "16px" }}>{String(params?.value || "-")}</span>;
      },
    },
    {
      field: "rideStatus",
      headerName: "Ride Status",
      minWidth: 130,
      sortable: true,
      renderCell: (params) => {
        const tone = getStatusBadgeStyle(params?.value);
        return <span style={{ background: tone.bg, color: tone.fg, borderRadius: 999, padding: "5px 10px", fontSize: 12, fontWeight: 700, textTransform: "uppercase", lineHeight: "16px" }}>{String(params?.value || "-")}</span>;
      },
    },
    { field: "createdAt", headerName: "Created", minWidth: 180, sortable: true },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 120,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <div className={classes.iconsWrapper}>
          <Button variant="contained" color="primary" size="small" style={{ minWidth: 36, padding: "4px 8px" }} onClick={() => handleEdit(params?.row?.raw)}>
            <Edit fontSize="small" />
          </Button>
          <Button variant="contained" color="error" size="small" style={{ minWidth: 36, padding: "4px 8px", backgroundColor: "#e5393526" }} onClick={() => handleDelete(params?.row?.raw)}>
            <Delete fontSize="small" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Grid container className={classes.root}>
      <Grid item xs={1} md={1} className={classes.funBtn}><Button variant="contained" startIcon={<ContentCopyOutlined />} onClick={handleCopy}>COPY</Button></Grid>
      <Grid item xs={1} md={1} className={classes.funBtn}><Button variant="contained" startIcon={<TableChartOutlined />} onClick={handleDownloadExcel}>EXCEL</Button></Grid>
      <Grid item xs={1} md={1} className={classes.funBtn}><Button variant="contained" startIcon={<DescriptionOutlined />} onClick={handleDownloadCSV}>CSV</Button></Grid>
      <Grid item xs={1} md={1} className={classes.funBtn}><Button variant="contained" startIcon={<PictureAsPdfOutlined />} onClick={handleDownloadPDF}>PDF</Button></Grid>
      <Grid item xs={2} md={2} className={classes.funBtn}><Button variant="contained" startIcon={<FileDownloadOutlined />} onClick={handleDownloadAll}>Download ALL</Button></Grid>
      <Grid item xs={6} md={6} className={classes.addBtn}>
        <Button variant="contained" color="primary" startIcon={<AddOutlined />} onClick={handleAdd}>
          Add Booking
        </Button>
      </Grid>

      <Grid item xs={12}>
        <section className={classes.dataGridTable}>
          <DataGrid
            rows={dataRows}
            columns={columns}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(1);
            }}
            onPageChange={(newPage) => {
              const p = parseInt(newPage, 10) + 1;
              if (p >= 1) setPage(p);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            pagination
            autoHeight
            disableSelectionOnClick
            scrollbarSize={4}
            rowHeight={60}
            style={{ padding: "10px", borderRadius: "0px", overflowX: "auto" }}
            paginationMode="server"
            rowCount={totalPages}
            filterMode="server"
            onFilterModelChange={(model) => setSearchText(model?.quickFilterValues?.join(" ") || "")}
            density="compact"
            components={{ Toolbar: CustomToolbar }}
          />
        </section>
      </Grid>
      <BookingCreateModal openDialog={addBookingModal} handleClose={handleCloseAdd} bookingId={selectedBookingId} />
      <BookingDeleteModal openDialog={deleteBookingModal} handleClose={handleCloseDelete} bookingId={selectedBookingId} />
    </Grid>
  );
};

export default Booking;
